import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { InstallationData } from "@/installation/installation";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { apiClient } from "@/api/client";
import { useSelectedOrganizationId } from "@/organization/api";
import { useRouter } from "@tanstack/react-router";
import { makeOptimisticHandlers } from "@/api/optimistic";
import { equipment } from "@/equipment/api";

type ListInstallationsFilters = {
  locationId?: number,
  sublocationId?: number,
  hasSensorKit?: boolean,
  sorting?: SortingState,
};

export const installations = createQueryKeys('installations', {
  detail: (
    installationId: number,
  ) => ({
    queryKey: [installationId],
    queryFn: async () => {
      return await get<InstallationData>(`installations/${installationId}/`);
    },
  }),
  list: ({ locationId, sublocationId, hasSensorKit, sorting = [] } : ListInstallationsFilters) => ({
    queryKey: ['list', locationId, sublocationId, hasSensorKit, sorting],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sublocationId !== undefined) {
        params.set('sublocation', sublocationId.toString());
      }
      if (locationId !== undefined) {
        params.set('location', locationId.toString());
      }
      if (hasSensorKit !== undefined) {
        params.set('has_sensor_kit', hasSensorKit.toString());
      }
      if (sorting.length) {
        params.set('ordering', `${sorting[0]?.desc ? "-" : ""}${sorting[0]?.id}`);
      }
      const url = `/installations/${params.toString() ? `?${params.toString()}` : ''}`;
      return await get<PaginatedResponse<InstallationData>>(url);
    },
  }),
});

export const useInstallations = ({ locationId, sublocationId, hasSensorKit, sorting = [] } : ListInstallationsFilters) => {
  return useSuspenseQuery(installations.list({ locationId, sublocationId, hasSensorKit, sorting }));
};

export const useCheckDuplicateInstallation = () => {
  return useMutation({
    mutationFn: async (params: {
      serialNumber: string;
      manufacturerName: string;
      modelName: string;
    }) => {
      const searchParams = new URLSearchParams({
        serial_number: params.serialNumber,
        manufacturer_name: params.manufacturerName,
        model_name: params.modelName,
        page_size: '1',
      });
      const res = await get<PaginatedResponse<InstallationData>>(
        `/installations/?${searchParams.toString()}`
      );
      return res.count > 0;
    },
  });
};

export const useInstallation = (installationId: number) => {
  return useSuspenseQuery(installations.detail(installationId));
};

export type CreateInstallationInput = {
  name: string;
  sublocation: number;
  location: number;
  equipment_model_id: number;
  serial_number?: string;
  installation_date?: string;
  status: string;
};

export const useCreateInstallation = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: CreateInstallationInput) => {
      const res = await apiClient.post(`organizations/${selectedOrganizationId}/locations/${data.location}/sublocations/${data.sublocation}/installations/`, data);
      return res.data as InstallationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: installations._def });
    },
  });
};

export type LinkSensorKitInput = {
  installation_id: number;
  location_id: number;
  sublocation_id: number;
  sensor_kit_serial_number: string;
  scan_id?: string;
};

export const useLinkSensorKit = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: LinkSensorKitInput) => {
      const res = await apiClient.post(`organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/installations/${data.installation_id}/link-sensor-kit/`, {
        sensor_kit_serial_number: data.sensor_kit_serial_number,
        scan_id: data.scan_id,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: installations._def });
    },
  });
};

export type UpdateInstallationInput = {
  installation_id: number;
  location_id: number;
  sublocation_id: number;
  phase_system_type?: string;
};

export const useUpdateInstallation = () => {
  const queryClient = useQueryClient()
  const selectedOrganizationId = useSelectedOrganizationId()
  const router = useRouter()

  return useMutation({
    mutationFn: async (data: UpdateInstallationInput) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/installations/${data.installation_id}/`,
        { phase_system_type: data.phase_system_type }
      )
      return res.data as InstallationData
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries(installations.detail(variables.installation_id))
      await router.invalidate()
      await router.navigate({ reloadDocument: true })
    },
  })
}

export const useMoveInstallation = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: { installation_id: number; location_id: number; sublocation_id: number; equipment_id: number }) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/installations/${data.installation_id}/`,
        { equipment_id: data.equipment_id }
      );
      return res.data as InstallationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: installations._def });
      queryClient.invalidateQueries({ queryKey: equipment._def });
    },
  });
};

export const useReorderInstallation = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: { installation_id: number; location_id: number; sublocation_id: number; position: number }) => {
      const res = await apiClient.post(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/installations/${data.installation_id}/reorder/`,
        { position: data.position }
      );
      return res.data as InstallationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipment._def });
    },
  });
};

export const useRenameInstallation = () => {
  const queryClient = useQueryClient()
  const selectedOrganizationId = useSelectedOrganizationId()

  return useMutation({
    mutationFn: async (data: { installation_id: number; location_id: number; sublocation_id: number; name: string }) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/installations/${data.installation_id}/`,
        { name: data.name }
      )
      return res.data as InstallationData
    },
    ...makeOptimisticHandlers<InstallationData, { installation_id: number; location_id: number; sublocation_id: number; name: string }>({
      queryClient,
      rootDef: installations._def,
      detailDef: installations.detail._def,
      listDef: installations.list._def,
      getId: (vars) => vars.installation_id,
      updater: (old, vars) => ({ ...old, name: vars.name }),
      extraInvalidate: [equipment._def],
    }),
  })
}